import { Button, Panel } from '@dev-spendesk/grapes';
import { type FormikProps } from 'formik';
import React, { type ReactNode } from 'react';

import { useModal } from 'common/hooks/useModal';
import { useTranslation } from 'common/hooks/useTranslation';
import { type VerificationOfPayeeResult } from 'src/core/modules/verification-of-payee/apis';
import { srcFromFile } from 'src/core/utils/files';
import { useIsVatEnabled } from 'src/core/utils/useIsVatEnabled';

import { BankInformationSection } from './BankInformationSection';
import { LegalInformationSection } from './LegalInformationSection';
import { ProofOfBankDetailsSection } from './ProofOfBankDetailsSection';
import { SupplierFormAccountPayableModalWarning } from './SupplierFormAccountPayableModalWarning';
import styles from '../panel.module.css';
import { type BankInfoOcrResult, type Supplier } from '../supplier';

export type FormMode = 'creation' | 'edition';

export type SupplierFormSectionId =
  | 'legalInformation'
  | 'bankInformation'
  | 'proofOfBankDetails';

export const DEFAULT_SECTIONS: SupplierFormSectionId[] = [
  'legalInformation',
  'bankInformation',
  'proofOfBankDetails',
];

export type FormValues = {
  supplier: {
    id?: string;
    name: string;
  } | null;
  accountHolderName: string | null;
  legalName: string | null;
  supplierAccountCode: string | null;
  registrationNumber: string | null;
  vatNumber: string | null;
  address: string | null;
  zipcode: string | null;
  city: string | null;
  country: string | null;
  bankCountry: string | null;
  iban: string | null;
  bic: string | null;
  sortCode: string | null;
  routingNumber: string | null;
  accountNumber: string | null;
  accountCode: string | null;
  bankInfoSuggestion: {
    iban: BankInfoOcrResult | null;
    bic: BankInfoOcrResult | null;
  };
  bankProofFile: {
    fileToUpload?: File;
    mimetype: string | null;
    filename: string | null;
    viewUrl: string | null;
    thumbnailUrl: string | null;
    downloadUrl: string | null;
  } | null;
};

export type RenderEditorFooterParams = {
  isLoading: boolean;
  onSave: () => void;
  type: FormMode;
};

type Props = {
  mode: FormMode;
  supplier?: Supplier;
  onShowBankProofFile: (
    bankProofFile?: Supplier['details']['bankProofFile'],
  ) => void;
  blockSubmit?: boolean;
  showBankProofFileInline: boolean;
  sections?: SupplierFormSectionId[];
  renderSupplierFormFooter?: (params: RenderEditorFooterParams) => ReactNode;
  verificationOfPayeeResult?: VerificationOfPayeeResult;
  onIgnoreVoP?: () => void;
  isCheckingVerificationOfPayee?: boolean;
};

export const SupplierForm = ({
  mode,
  onShowBankProofFile,
  blockSubmit = false,
  sections = DEFAULT_SECTIONS,
  renderSupplierFormFooter,
  // Formik props
  values,
  errors,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  supplier,
  showBankProofFileInline,
  verificationOfPayeeResult,
  onIgnoreVoP,
  isCheckingVerificationOfPayee,
}: Props & FormikProps<FormValues>) => {
  const { t } = useTranslation('global');
  const isVatEnabled = useIsVatEnabled(values.country);
  const [
    accountPayableWarningModal,
    showAccountPayableWarningModal,
    hideAccountPayableWarningModal,
  ] = useModal(() => (
    <SupplierFormAccountPayableModalWarning
      onConfirm={() => handleSubmit()}
      onCancel={() => hideAccountPayableWarningModal()}
    />
  ));

  const handleUploadBankProofFile = (file: File) => {
    if (!file) {
      return;
    }
    setFieldValue('bankProofFile', {
      fileToUpload: file,
      mimetype: file.type,
      filename: file.name,
      viewUrl: srcFromFile(file),
      thumbnailUrl: srcFromFile(file),
    });
  };

  const handleDeleteBankProofFile = () => {
    setFieldValue('bankProofFile', null);
    onShowBankProofFile();
  };

  const handleSubmitControl = (
    event: React.FormEvent<HTMLFormElement>,
  ): void | undefined => {
    event.preventDefault();
    if (
      supplier?.supplierAccountCode &&
      values.supplierAccountCode &&
      supplier?.supplierAccountCode !== values.supplierAccountCode.trim()
    ) {
      showAccountPayableWarningModal();
    } else {
      handleSubmit(event);
    }
  };

  const sectionComponents: Record<SupplierFormSectionId, React.ReactNode> = {
    legalInformation: (
      <LegalInformationSection
        key="legalInformation"
        values={values}
        errors={errors}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        isVatEnabled={isVatEnabled}
      />
    ),
    bankInformation: (
      <BankInformationSection
        key="bankInformation"
        values={values}
        errors={errors}
        setFieldValue={setFieldValue}
        isCheckingVerificationOfPayee={isCheckingVerificationOfPayee}
        verificationOfPayeeResult={verificationOfPayeeResult}
        onIgnoreVoP={onIgnoreVoP}
      />
    ),
    proofOfBankDetails: (
      <ProofOfBankDetailsSection
        key="proofOfBankDetails"
        values={values}
        onShowBankProofFile={onShowBankProofFile}
        onUploadBankProofFile={handleUploadBankProofFile}
        onDeleteBankProofFile={handleDeleteBankProofFile}
        showBankProofFileInline={showBankProofFileInline}
      />
    ),
  };

  return (
    <form
      className="overflow-hidden"
      onSubmit={handleSubmitControl}
      noValidate
      data-mask
    >
      <Panel
        className={styles.panel}
        footer={
          renderSupplierFormFooter ? (
            renderSupplierFormFooter({
              isLoading: isSubmitting,
              onSave: () => handleSubmit(),
              type: mode,
            })
          ) : (
            <Button
              fit="parent"
              type="submit"
              text={
                mode === 'creation'
                  ? t('submitMyInvoice.supplier.createSupplier')
                  : t('submitMyInvoice.supplier.updateSupplier')
              }
              isLoading={isSubmitting}
              isDisabled={isSubmitting || blockSubmit}
            />
          )
        }
      >
        {sections.map((sectionId) => (
          <React.Fragment key={sectionId}>
            {sectionComponents[sectionId]}
          </React.Fragment>
        ))}
        {accountPayableWarningModal}
      </Panel>
    </form>
  );
};
